/**
 * Live-World primitives · see DESIGN_LANGUAGE_v2 §6.
 * Compact, reusable, framer-motion-driven.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DUR, EASE, ROLE_ACCENT } from "../../lib/motion";

export function BreathingDot({ size = 8, color, testId = "live-breathing-dot" }) {
  const c = color || ROLE_ACCENT.admin;
  return (
    <motion.span
      data-testid={testId}
      animate={{ scale: [1, 1.4, 1], opacity: [0.9, 0.6, 0.9] }}
      transition={{ duration: 1.6, repeat: Infinity, ease: EASE.emerge }}
      style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", background: c, boxShadow: `0 0 6px ${c}` }}
    />
  );
}

export function BeaconHalo({ color = ROLE_ACCENT.driver, size = 200 }) {
  return (
    <div data-testid="live-beacon-halo" style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      {[0, 0.4, 0.8].map((delay, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0.5, scale: 0.4 }}
          animate={{ opacity: [0.6, 0], scale: [0.4, 1.6] }}
          transition={{ duration: 1.8, repeat: Infinity, delay, ease: EASE.linear }}
          style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color }}
        />
      ))}
      <div style={{
        position: "absolute", inset: size * 0.22, borderRadius: "50%", background: color,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.18, fontWeight: 900, color: "#0B1220",
      }}>ON</div>
    </div>
  );
}

export function AnonymizedTicker({ items, intervalMs = 6000 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(v => (v + 1) % items.length), intervalMs);
    return () => clearInterval(t);
  }, [items.length, intervalMs]);
  return (
    <div data-testid="live-ticker-root" style={{ overflow: "hidden", height: 22, position: "relative" }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ y: 22, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -22, opacity: 0 }}
          transition={{ duration: DUR.smooth, ease: EASE.emerge }}
          style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.04em" }}
        >
          {items[i]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function PulseOnChange({ value, color, children }) {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 400);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <motion.div
      data-testid="live-pulse-target"
      animate={pulse ? { boxShadow: `0 0 40px 0 ${color || ROLE_ACCENT.admin}` } : { boxShadow: "0 0 0 0 transparent" }}
      transition={{ duration: DUR.smooth, ease: EASE.settle }}
      style={{ display: "inline-block" }}
    >
      {children}
    </motion.div>
  );
}

export function ChainOfCustodyStitcher({ rows, accent = ROLE_ACCENT.driver }) {
  // rows: [{label, stamp, provider}]
  return (
    <div data-testid="live-coc-stitcher" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((r, idx) => (
        <motion.div
          key={r.label + idx}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: DUR.reveal, ease: EASE.pop, delay: idx * 0.2 }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 12px", borderRadius: 8,
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent, boxShadow: `0 0 6px ${accent}` }}/>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#E2E8F0", flex: 1 }}>{r.label}</span>
          <span style={{ fontSize: 10, color: "#94A3B8", fontFamily: "ui-monospace,Menlo,monospace" }}>{r.stamp}</span>
          {r.provider && <span style={{ fontSize: 9, color: accent, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 800 }}>{r.provider}</span>}
        </motion.div>
      ))}
    </div>
  );
}
